import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Building2, ChevronLeft, Copy, ExternalLink, FilePenLine, FileText, FolderPlus, Link2, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import { useAuth } from '@/components/Auth/AuthContext';
import ProjectFolderSetupNotice from '@/components/projects/ProjectFolderSetupNotice';
import { hasRequiredRoleAccess } from '@/utils/swaRole';
import { DEFAULT_QUOTATION_TEMPLATE_DEFINITION } from '@/constants/quotationOptions';
import {
  normalizeSuburbLookupValue,
  searchSuburbOptions,
  searchSuburbOptionsByPostCode,
  type SuburbLookupRow,
} from '@/services/suburbLookup';
import { confirmDeletionByTyping } from '@/utils/deleteConfirmation';
import { formatPhoneForDisplay, normalizePhoneForStorage } from '@/utils/phone';
import { isAcceptedQuotationLike, isAcceptedStatusValue } from '@/utils/quotationAcceptance';
import { getQuotationStructuralFeePerUnit, getQuotationStructuralFeeTotal } from '@/utils/quotationFees';
import { formatSuburbLabel } from '@/utils/suburbText';
import { Button, DetailActionMenu, DetailFieldList, FormSection, StatusBadge } from '@/ui/components';
import '@/pages/dataGridFocus.css';

/*
 * Quotation detail screen.
 *
 * Plain-English view:
 * - Shows one quotation and most of its business fields.
 * - Lets the user edit the quotation, create folders, accept it into a project, restore linked archived projects, or soft-delete it.
 * - This screen now loads one quotation directly by id, so it no longer depends on the first page of the quotation list containing the record.
 */
type DetailSection = {
  title: string;
  icon: React.ReactNode;
  accentColor: string;
  fields: Array<{
    label: string;
    value: React.ReactNode;
    wrap?: boolean;
    emphasize?: boolean;
  }>;
};

type QuotationSigningLinkResult = {
  sessionId: number;
  quotationId: number;
  quotationNumber: string | null;
  status: string;
  signUrl: string;
  issuedAt: string;
  expiresAt: string;
};

type QuotationEditDraft = {
  quotationNumber: string;
  date: string;
  acceptance: string;
  broughtBy: string;
  certifiedBy: string;
  address: string;
  suburb: string;
  postCode: string;
  council: string;
  projectScope: string;
  projectType: string;
  projectDescription: string;
  structuralScopeFootnote: string;
  civilScopeFootnote: string;
  noUnits: string;
  structuralFee: string;
  civilFee: string;
  soilReportFee: string;
  civil: string;
  certificates: string;
  adjustment: string;
  structuralTotal: string;
  structuralReferral: string;
  structuralSubTotal: string;
  subTotal: string;
  client: string;
  clientCompany: string;
  abn: string;
  clientAddress: string;
  clientMobile: string;
  clientEmail: string;
  exclusions: string;
  entity: string;
  senderDisplayName: string;
  senderAddress: string;
  senderMobile: string;
  senderEmail: string;
  senderPayId: string;
  senderBsb: string;
  senderAccountNumber: string;
  senderAccountName: string;
  referralName: string;
  referralPercentage: string;
  referralFee: string;
  referralAmount: string;
  architectName: string;
  architectCompany: string;
  architectAbn: string;
  architectAddress: string;
  architectMobile: string;
  architectEmail: string;
};

type ContactLookupRole = 'referral' | 'architect';

type ClientLookupCandidate = {
  ClientId?: number | string | null;
  Name: string | null;
  Company: string | null;
  Email: string | null;
  Phone: string | null;
  Address: string | null;
  Abn: string | null;
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '100%',
  padding: '10px 12px',
  borderRadius: '10px',
  border: '1px solid var(--color-border)',
  fontSize: '14px',
  lineHeight: 1.4,
  color: 'var(--color-text)',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-surface)',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: '96px',
  resize: 'vertical',
  fontFamily: 'inherit',
};

const contactLookupWrapStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
};

const contactLookupDropdownStyle: React.CSSProperties = {
  position: 'absolute',
  zIndex: 30,
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  border: '1px solid var(--color-border)',
  borderRadius: 12,
  background: 'var(--color-surface)',
  boxShadow: '0 16px 32px rgba(15,23,42,0.16)',
  overflow: 'hidden',
};

const contactLookupHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '8px 10px',
  borderBottom: '1px solid var(--color-border)',
  color: 'var(--color-text-muted)',
  fontSize: 12,
};

const contactLookupCloseStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  background: 'var(--color-surface2)',
  color: 'var(--color-text)',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 700,
  padding: '4px 8px',
};

const contactLookupOptionStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  borderBottom: '1px solid var(--color-border)',
  background: 'transparent',
  color: 'var(--color-text)',
  cursor: 'pointer',
  padding: '10px 12px',
  textAlign: 'left',
};

function toInputString(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function toDateInputValue(value: unknown): string {
  if (!value) return '';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function normalizePostCode(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(Math.trunc(value)) : '';
  }
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? String(Math.trunc(numeric)) : trimmed;
}

function normalizeContactLookupValue(value: unknown): string {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function parseExclusions(value: any): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((item) => String(item)).filter(Boolean);
  try {
    const parsed = JSON.parse(String(value));
    return Array.isArray(parsed) ? parsed.map((item) => String(item)).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function createDraftFromQuotation(quotation: any): QuotationEditDraft {
  return {
    quotationNumber: toInputString(quotation?.QuotationNumber ?? quotation?.QuotationNo),
    date: toDateInputValue(quotation?.Date),
    acceptance: toInputString(quotation?.Acceptance || (isAcceptedQuotationLike(quotation) ? 'Accepted' : '')),
    broughtBy: toInputString(quotation?.BroughtBy),
    certifiedBy: toInputString(quotation?.CertifiedBy),
    address: toInputString(quotation?.Address),
    suburb: toInputString(quotation?.Suburb),
    postCode: normalizePostCode(quotation?.PostCode),
    council: toInputString(quotation?.Council),
    projectScope: toInputString(quotation?.ProjectScope),
    projectType: toInputString(quotation?.ProjectType),
    projectDescription: toInputString(quotation?.ProjectDescription),
    structuralScopeFootnote: toInputString(quotation?.ScopeFootnoteS ?? quotation?.Scope_footnote_S),
    civilScopeFootnote: toInputString(quotation?.ScopeFootnoteC ?? quotation?.Scope_footnote_C),
    noUnits: toInputString(quotation?.NoUnits),
    structuralFee: toInputString(quotation?.StructuralFee),
    civilFee: toInputString(quotation?.CivilFee),
    soilReportFee: toInputString(quotation?.SoilReportFee),
    civil: toInputString(quotation?.Civil),
    certificates: toInputString(quotation?.Certificates),
    adjustment: toInputString(quotation?.Adjustment),
    structuralTotal: toInputString(quotation?.Structural ?? getQuotationStructuralFeeTotal(quotation)),
    structuralReferral: toInputString(quotation?.StructuralReferral),
    structuralSubTotal: toInputString(quotation?.StructuralSubTotal),
    subTotal: toInputString(quotation?.SubTotal),
    client: toInputString(quotation?.Client),
    clientCompany: toInputString(quotation?.ClientCompany),
    abn: toInputString(quotation?.ABN),
    clientAddress: toInputString(quotation?.ClientAddress),
    clientMobile: formatPhoneForDisplay(quotation?.ClientMobile),
    clientEmail: toInputString(quotation?.ClientEmail),
    exclusions: parseExclusions(quotation?.ExclusionsJson).join('\n'),
    entity: toInputString(quotation?.Entity),
    senderDisplayName: toInputString(quotation?.SenderDisplayName),
    senderAddress: toInputString(quotation?.SenderAddress),
    senderMobile: formatPhoneForDisplay(quotation?.SenderMobile),
    senderEmail: toInputString(quotation?.SenderEmail),
    senderPayId: toInputString(quotation?.SenderPayId),
    senderBsb: toInputString(quotation?.SenderBsb),
    senderAccountNumber: toInputString(quotation?.SenderAccountNumber),
    senderAccountName: toInputString(quotation?.SenderAccountName),
    referralName: toInputString(quotation?.ReferralName),
    referralPercentage: quotation?.ReferralPercentage || quotation?.ReferralPercentage === 0
      ? String(Number(quotation.ReferralPercentage) * 100)
      : '',
    referralFee: toInputString(quotation?.ReferralFee),
    referralAmount: toInputString(quotation?.ReferralAmount),
    architectName: toInputString(quotation?.ArchitectName),
    architectCompany: toInputString(quotation?.ArchitectCompany),
    architectAbn: toInputString(quotation?.ArchitectABN),
    architectAddress: toInputString(quotation?.ArchitectAddress),
    architectMobile: formatPhoneForDisplay(quotation?.ArchitectMobile),
    architectEmail: toInputString(quotation?.ArchitectEmail),
  };
}

function formatFieldValue(value: any) {
  if (value === null || value === undefined) return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' && (value.includes('T') || value.includes('/'))) {
    try {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
      }
    } catch {}
  }
  return String(value);
}

function formatAmount(amount?: number | string) {
  if (!amount && amount !== 0) return '$0.00';
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(numAmount);
}

function formatPercentage(value?: number) {
  if (!value && value !== 0) return '0%';
  return `${(value * 100).toFixed(2)}%`;
}

function parseOptionalNumberInput(value: string, label: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    throw new Error(`${label} must be a valid number.`);
  }
  return parsed;
}

function parseLooseNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeStatusValue(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function isProjectStatusBlockingQuotationRecovery(status: unknown): boolean {
  const normalized = normalizeStatusValue(status);
  if (!normalized) {
    return true;
  }

  return !['cancelled', 'canceled', 'deleted'].includes(normalized);
}

function isProjectStatusRestorable(status: unknown): boolean {
  const normalized = normalizeStatusValue(status);
  return normalized === 'cancelled' || normalized === 'canceled';
}

function isArchivedProjectRecord(project: any): boolean {
  if (!project) {
    return false;
  }

  return Number(project.IsDeleted ?? 0) === 1
    || Boolean(project.DeletedDate)
    || isProjectStatusRestorable(project.Status)
    || normalizeStatusValue(project.Status) === 'deleted';
}

function getDraftUnitCount(draft: Pick<QuotationEditDraft, 'noUnits'> | null | undefined): number {
  const units = parseLooseNumber(draft?.noUnits);
  return units && units > 0 ? Math.max(1, Math.trunc(units)) : 1;
}

function getDraftStructuralTotal(draft: Pick<QuotationEditDraft, 'structuralTotal'> | null | undefined): number | null {
  return parseLooseNumber(draft?.structuralTotal);
}

function getDraftStructuralFeePerUnitValue(
  draft: Pick<QuotationEditDraft, 'noUnits' | 'structuralTotal'> | null | undefined
): number | null {
  const structuralTotal = getDraftStructuralTotal(draft);
  if (structuralTotal === null) {
    return null;
  }

  const units = getDraftUnitCount(draft);
  return Number((structuralTotal / units).toFixed(2));
}

const WILSON_EMAIL = 'wd@linfinityeng.com';
const YUAN_EMAIL = 'yl@linfinityeng.com';

const QuotationDetail: React.FC = () => {
  const { quotationId } = useParams<{ quotationId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [quotation, setQuotation] = useState<any>(null);
  const [draft, setDraft] = useState<QuotationEditDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [folderCreating, setFolderCreating] = useState(false);
  const [projectCreating, setProjectCreating] = useState(false);
  const [projectFolderCreating, setProjectFolderCreating] = useState(false);
  const [folderSetupRefresh, setFolderSetupRefresh] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [quoteViewBusy, setQuoteViewBusy] = useState(false);
  const [signingLinkBusy, setSigningLinkBusy] = useState(false);
  const [signingLinkResult, setSigningLinkResult] = useState<QuotationSigningLinkResult | null>(null);
  const [signingLinkError, setSigningLinkError] = useState<string | null>(null);
  const [signingLinkNotice, setSigningLinkNotice] = useState<string | null>(null);
  const [quotationTemplateDefinition, setQuotationTemplateDefinition] = useState(DEFAULT_QUOTATION_TEMPLATE_DEFINITION);
  const [suburbOptions, setSuburbOptions] = useState<SuburbLookupRow[]>([]);
  const [suburbLookupMessage, setSuburbLookupMessage] = useState('');
  const [contactLookupRole, setContactLookupRole] = useState<ContactLookupRole | null>(null);
  const [contactLookupMatches, setContactLookupMatches] = useState<ClientLookupCandidate[]>([]);
  const [contactLookupBusy, setContactLookupBusy] = useState(false);
  const [contactLookupMessage, setContactLookupMessage] = useState('');
  const [closedContactLookupKey, setClosedContactLookupKey] = useState('');
  const [linkedProject, setLinkedProject] = useState<any>(null);
  const [linkedProjectLoading, setLinkedProjectLoading] = useState(false);
  const [linkedProjectLookupFailed, setLinkedProjectLookupFailed] = useState(false);
  const [projectRestoring, setProjectRestoring] = useState(false);
  const currentUserEmail = String(user?.userDetails || '').trim().toLowerCase();
  const resolvedQuotationRecordId = quotation?.QuotationId ?? quotation?.Id ?? null;
  const resolvedQuotationNumber = String(quotation?.QuotationNumber || quotation?.QuotationNo || '').trim();

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    void loadQuotationDetails();
  }, [isAuthenticated, user, quotationId]);

  useEffect(() => {
    let active = true;

    const loadTemplateDefinition = async () => {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const response = await azureSqlService.getQuotationTemplateDefinition();
        if (!active) return;
        setQuotationTemplateDefinition(response?.definition || DEFAULT_QUOTATION_TEMPLATE_DEFINITION);
      } catch {
        if (!active) return;
        setQuotationTemplateDefinition(DEFAULT_QUOTATION_TEMPLATE_DEFINITION);
      }
    };

    void loadTemplateDefinition();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isEditing || !draft) {
      setSuburbOptions([]);
      return;
    }

    const suburb = draft.suburb.trim();
    if (suburb.length < 2) {
      setSuburbOptions([]);
      return;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        const matches = await searchSuburbOptions(suburb, 8);
        if (!active) return;
        setSuburbOptions(matches);

        const exactMatch = matches.find(
          (row) => normalizeSuburbLookupValue(row.Suburb) === normalizeSuburbLookupValue(suburb)
        );
        if (exactMatch) {
          setDraft((current) => (
            current ? {
              ...current,
              suburb: exactMatch.Suburb || current.suburb,
              postCode: exactMatch.PostCode !== null && exactMatch.PostCode !== undefined
                ? String(exactMatch.PostCode)
                : current.postCode,
              council: exactMatch.Council || current.council,
            } : current
          ));
          setSuburbLookupMessage(`Postcode${exactMatch.Council ? ' and council' : ''} filled from suburb lookup.`);
        }
      } catch {
        if (!active) return;
        setSuburbOptions([]);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [draft?.suburb, isEditing]);

  useEffect(() => {
    if (!isEditing || !draft || !contactLookupRole) {
      setContactLookupMatches([]);
      setContactLookupBusy(false);
      return;
    }

    const lookup = contactLookupRole === 'referral' ? draft.referralName.trim() : draft.architectName.trim();
    const lookupKey = `${contactLookupRole}:${normalizeContactLookupValue(lookup)}`;
    if (lookup.length < 2 || lookupKey === closedContactLookupKey) {
      setContactLookupMatches([]);
      setContactLookupBusy(false);
      if (lookup.length < 2) {
        setContactLookupMessage('');
      }
      return;
    }

    let active = true;
    setContactLookupBusy(true);

    const timer = window.setTimeout(async () => {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const matches = await azureSqlService.searchClients(lookup, 8);
        if (!active) return;

        const normalized = Array.isArray(matches)
          ? matches.map((row: any) => ({
              ClientId: row?.ClientId ?? null,
              Name: String(row?.Name || row?.Client || '').trim() || null,
              Company: String(row?.Company || row?.ClientCompany || row?.CompanyName || '').trim() || null,
              Email: String(row?.Email || row?.ClientEmail || '').trim() || null,
              Phone: formatPhoneForDisplay(row?.Phone || row?.ClientMobile || row?.Mobile || '') || null,
              Address: String(row?.Address || row?.ClientAddress || '').trim() || null,
              Abn: String(row?.Abn || row?.ABN || row?.ClientAbn || '').trim() || null,
            })).filter((row: ClientLookupCandidate) => Boolean(row.Name || row.Company || row.Email || row.Phone))
          : [];

        setContactLookupMatches(normalized);
        setContactLookupMessage(
          normalized.length > 0
            ? `Found ${normalized.length} matching client${normalized.length === 1 ? '' : 's'}.`
            : 'No matching clients found.'
        );
      } catch {
        if (!active) return;
        setContactLookupMatches([]);
        setContactLookupMessage('Client lookup is unavailable right now.');
      } finally {
        if (active) {
          setContactLookupBusy(false);
        }
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [closedContactLookupKey, contactLookupRole, draft?.architectName, draft?.referralName, isEditing]);

  const loadQuotationDetails = async (background = false): Promise<any | null> => {
    if (!quotationId) return null;

    if (!background) {
      setLoading(true);
      setError(null);
    } else {
      setSaveError(null);
    }

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const foundQuotation = await azureSqlService.getQuotationById(quotationId, { useCache: false });

      if (!foundQuotation) {
        if (background) {
          setSaveError('Quotation not found during folder refresh.');
          return null;
        }
        setError('Quotation not found');
        setLinkedProject(null);
        setLinkedProjectLoading(false);
        setLinkedProjectLookupFailed(false);
        return null;
      }

      const nextLinkedProjectId = Number(foundQuotation.LinkedProjectId || foundQuotation.ProjectId || 0) || null;
      if (!background) {
        setLinkedProject(null);
        setLinkedProjectLoading(Boolean(nextLinkedProjectId));
      }
      setLinkedProjectLookupFailed(false);

      if (nextLinkedProjectId) {
        try {
          const linkedProjectRecord = await azureSqlService.getProjectById(nextLinkedProjectId, { useCache: false });
          setLinkedProject(linkedProjectRecord || null);
        } catch (projectError) {
          const statusCode = Number((projectError as { status?: unknown } | null)?.status);
          const message = projectError instanceof Error ? projectError.message : 'Unknown error';

          if (statusCode === 404 || /not found/i.test(message)) {
            setLinkedProject(null);
          } else {
            console.warn('Failed to load linked project from quotation detail.', projectError);
            setLinkedProject({
              ProjectId: nextLinkedProjectId,
              Status: 'Unknown',
            });
            setLinkedProjectLookupFailed(true);
          }
        } finally {
          setLinkedProjectLoading(false);
        }
      } else {
        setLinkedProjectLoading(false);
      }

      setQuotation(foundQuotation);
      if (!isEditing) {
        setDraft(createDraftFromQuotation(foundQuotation));
      }
      return foundQuotation;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      if (background) {
        setSaveError(`Failed to refresh quotation: ${errorMessage}`);
      } else {
        setError(`Failed to load quotation: ${errorMessage}`);
      }
      return null;
    } finally {
      if (!background) setLoading(false);
    }
  };

  const updateDraft = (key: keyof QuotationEditDraft, value: string) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  const closeContactLookup = (role: ContactLookupRole) => {
    const lookup = role === 'referral' ? draft?.referralName : draft?.architectName;
    setClosedContactLookupKey(`${role}:${normalizeContactLookupValue(lookup)}`);
    setContactLookupMatches([]);
    setContactLookupBusy(false);
    setContactLookupMessage('');
  };

  const applyContactLookupMatch = (role: ContactLookupRole, match: ClientLookupCandidate) => {
    const displayName = match.Name || match.Company || '';
    setDraft((current) => {
      if (!current) return current;
      if (role === 'referral') {
        return {
          ...current,
          referralName: displayName || current.referralName,
        };
      }

      return {
        ...current,
        architectName: displayName || current.architectName,
        architectCompany: match.Company || current.architectCompany,
        architectAbn: match.Abn || current.architectAbn,
        architectAddress: match.Address || current.architectAddress,
        architectMobile: formatPhoneForDisplay(match.Phone) || current.architectMobile,
        architectEmail: match.Email || current.architectEmail,
      };
    });
    setContactLookupRole(null);
    setClosedContactLookupKey('');
    setContactLookupMatches([]);
    setContactLookupBusy(false);
    setContactLookupMessage(`Picked ${displayName || 'client'} from the client list.`);
  };

  const hydrateSuburbLookup = async (rawSuburb: string) => {
    const suburb = rawSuburb.trim();
    if (suburb.length < 2) {
      setSuburbLookupMessage('');
      return;
    }

    try {
      const matches = await searchSuburbOptions(suburb, 8);
      setSuburbOptions(matches);

      const exactMatch = matches.find(
        (row) => normalizeSuburbLookupValue(row.Suburb) === normalizeSuburbLookupValue(suburb)
      );
      if (!exactMatch) {
        setSuburbLookupMessage('No exact suburb match found yet. Keep typing or pick from the list.');
        return;
      }

      setDraft((current) => (
        current ? {
          ...current,
          suburb: exactMatch.Suburb || current.suburb,
          postCode: exactMatch.PostCode !== null && exactMatch.PostCode !== undefined
            ? String(exactMatch.PostCode)
            : current.postCode,
          council: exactMatch.Council || current.council,
        } : current
      ));
      setSuburbLookupMessage(`Postcode${exactMatch.Council ? ' and council' : ''} filled from suburb lookup.`);
    } catch {
      setSuburbLookupMessage('Suburb lookup is unavailable right now.');
    }
  };

  const hydratePostCodeLookup = async (rawPostCode: string) => {
    const postCode = normalizePostCode(rawPostCode);
    if (postCode.length < 3) {
      setSuburbLookupMessage('');
      return;
    }

    try {
      const matches = await searchSuburbOptionsByPostCode(postCode, 20);
      setSuburbOptions(matches);

      if (matches.length === 0) {
        setSuburbLookupMessage('No suburb match found for that postcode yet.');
        return;
      }

      const exactSuburbMatch = matches.find(
        (row) => normalizeSuburbLookupValue(row.Suburb) === normalizeSuburbLookupValue(draft?.suburb)
      );
      const selectedMatch = exactSuburbMatch || (matches.length === 1 ? matches[0] : null);

      if (!selectedMatch) {
        setDraft((current) => (
          current ? {
            ...current,
            postCode,
          } : current
        ));
        setSuburbLookupMessage('Multiple suburbs match that postcode. Pick the suburb from the list.');
        return;
      }

      setDraft((current) => (
        current ? {
          ...current,
          suburb: selectedMatch.Suburb || current.suburb,
          postCode: selectedMatch.PostCode !== null && selectedMatch.PostCode !== undefined
            ? String(selectedMatch.PostCode)
            : postCode,
          council: selectedMatch.Council || current.council,
        } : current
      ));
      setSuburbLookupMessage(`Suburb${selectedMatch.Council ? ', postcode, and council' : ' and postcode'} filled from lookup.`);
    } catch {
      setSuburbLookupMessage('Suburb lookup is unavailable right now.');
    }
  };

  const formatDraftPhoneField = (key: keyof Pick<QuotationEditDraft, 'clientMobile' | 'senderMobile' | 'architectMobile'>) => {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        [key]: normalizePhoneForStorage(current[key]) || '',
      };
    });
  };

  const renderDraftField = (
    key: keyof QuotationEditDraft,
    options: {
      type?: 'text' | 'textarea' | 'number' | 'date' | 'email' | 'tel';
      rows?: number;
      placeholder?: string;
    } = {}
  ) => {
    const value = draft?.[key] ?? '';
    if (options.type === 'textarea') {
      return (
        <textarea
          rows={options.rows ?? 4}
          value={value}
          placeholder={options.placeholder}
          onChange={(event) => updateDraft(key, event.target.value)}
          style={textareaStyle}
        />
      );
    }

    return (
      <input
        type={options.type || 'text'}
        value={value}
        placeholder={options.placeholder}
        onChange={(event) => updateDraft(key, event.target.value)}
        style={inputStyle}
      />
    );
  };

  const renderContactLookupDraftField = (role: ContactLookupRole) => {
    const fieldKey: keyof QuotationEditDraft = role === 'referral' ? 'referralName' : 'architectName';
    const value = draft?.[fieldKey] ?? '';
    const isOpen = contactLookupRole === role;
    const showDropdown = isOpen && (
      contactLookupBusy
      || contactLookupMatches.length > 0
      || Boolean(contactLookupMessage)
    );

    return (
      <div style={contactLookupWrapStyle}>
        <input
          type="text"
          value={value}
          placeholder={role === 'referral' ? 'Search client list for referral' : 'Search client list for architect'}
          onFocus={() => {
            setContactLookupRole(role);
            if (`${role}:${normalizeContactLookupValue(value)}` !== closedContactLookupKey) {
              setContactLookupMessage('');
            }
          }}
          onChange={(event) => {
            setContactLookupRole(role);
            setClosedContactLookupKey('');
            updateDraft(fieldKey, event.target.value);
          }}
          style={inputStyle}
        />
        {showDropdown ? (
          <div style={contactLookupDropdownStyle}>
            <div style={contactLookupHeaderStyle}>
              <span>
                {contactLookupBusy ? 'Searching clients...' : contactLookupMessage || 'Pick from client list'}
              </span>
              <button
                type="button"
                onClick={() => closeContactLookup(role)}
                style={contactLookupCloseStyle}
              >
                Close
              </button>
            </div>
            {contactLookupMatches.map((match) => {
              const title = match.Name || match.Company || 'Unnamed client';
              const subtitle = [
                match.Company && match.Company !== title ? match.Company : null,
                match.Email,
                match.Phone,
              ].filter(Boolean).join(' | ');
              const key = `${match.ClientId ?? title}-${match.Email ?? match.Phone ?? match.Address ?? ''}`;

              return (
                <button
                  key={key}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyContactLookupMatch(role, match)}
                  style={contactLookupOptionStyle}
                >
                  <div style={{ fontWeight: 700 }}>{title}</div>
                  {subtitle ? (
                    <div style={{ marginTop: 3, fontSize: 12, color: 'var(--color-text-muted)' }}>
                      {subtitle}
                    </div>
                  ) : null}
                  {match.Address ? (
                    <div style={{ marginTop: 3, fontSize: 12, color: 'var(--color-text-muted)' }}>
                      {match.Address}
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  const renderComputedDraftField = (value: string, helperText?: string) => (
    <div style={{ display: 'grid', gap: '8px' }}>
      <input
        type="text"
        value={value}
        readOnly
        style={{
          ...inputStyle,
          backgroundColor: 'var(--color-surface2)',
          color: 'var(--color-text-muted)',
        }}
      />
      {helperText ? (
        <div className="dgf-inline-help">
          {helperText}
        </div>
      ) : null}
    </div>
  );

  const handleStartEditing = () => {
    if (!quotation) return;
    setDraft(createDraftFromQuotation(quotation));
    setSaveError(null);
    setSaveNotice(null);
    setSuburbLookupMessage('');
    setContactLookupRole(null);
    setContactLookupMatches([]);
    setContactLookupMessage('');
    setClosedContactLookupKey('');
    setIsEditing(true);
  };

  const handleAmendInWorkflow = () => {
    if (!resolvedQuotationRecordId) return;
    navigate('/quotations/workflow', {
      state: {
        amendQuotation: {
          quotationId: String(resolvedQuotationRecordId),
          quotationNumber: resolvedQuotationNumber,
        },
      },
    });
  };

  const handleCancelEditing = () => {
    setDraft(quotation ? createDraftFromQuotation(quotation) : null);
    setSaveError(null);
    setSaveNotice(null);
    setSuburbLookupMessage('');
    setContactLookupRole(null);
    setContactLookupMatches([]);
    setContactLookupMessage('');
    setClosedContactLookupKey('');
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!quotation || !draft) return;

    const quotationNumber = draft.quotationNumber.trim();
    if (!quotationNumber) {
      setSaveError('Quotation number is required.');
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);
      setSaveNotice(null);

      const exclusions = draft.exclusions
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);

      const numberOfUnits = (() => {
        const parsed = parseOptionalNumberInput(draft.noUnits, 'Number of units');
        return parsed === null ? null : Math.max(1, Math.trunc(parsed));
      })();
      const structuralTotal = parseOptionalNumberInput(draft.structuralTotal, 'Structural fee total');
      const structuralFeePerUnit = structuralTotal === null
        ? null
        : Number((structuralTotal / (numberOfUnits && numberOfUnits > 0 ? numberOfUnits : 1)).toFixed(2));

      const nextAcceptance = draft.acceptance.trim();
      const quotationWasAccepted = isAcceptedQuotationLike(quotation);
      const normalizedNextAcceptance = nextAcceptance.toLowerCase();
      const acceptanceReversal = quotationWasAccepted
        && (normalizedNextAcceptance === 'pending' || normalizedNextAcceptance === 'rejected');
      const acceptanceTriggersWorkflow = !quotationWasAccepted && isAcceptedStatusValue(nextAcceptance);
      const acceptanceValueForSave = acceptanceTriggersWorkflow
        ? null
        : (nextAcceptance || null);
      if (acceptanceTriggersWorkflow) {
        const confirmed = window.confirm(
          `Accept quotation ${quotationNumber}?\n\nThis will save your changes, create the linked project, and run the current project folder creation workflow. If the matching SharePoint project folder already exists, acceptance will continue and the duplicate folder step will be skipped.`
        );
        if (!confirmed) {
          setSaving(false);
          return;
        }
      }
      if (acceptanceReversal) {
        const confirmed = window.confirm(
          `Change accepted quotation ${quotationNumber} to ${nextAcceptance}?\n\nThe signed PDF, signing audit record, linked project, folders, tasks, and invoices will be preserved. Pending quotations can issue a new signing link.`
        );
        if (!confirmed) {
          setSaving(false);
          return;
        }
      }

      const payload = {
        quotationNumber,
        date: draft.date || null,
        ...(acceptanceValueForSave === null ? {} : { acceptance: acceptanceValueForSave }),
        broughtBy: draft.broughtBy.trim() || null,
        certifiedBy: draft.certifiedBy.trim() || null,
        address: draft.address.trim() || null,
        suburb: draft.suburb.trim() || null,
        postCode: parseOptionalNumberInput(draft.postCode, 'Post code'),
        council: draft.council.trim() || null,
        projectScope: draft.projectScope.trim() || null,
        projectType: draft.projectType.trim() || null,
        projectDescription: draft.projectDescription.trim() || null,
        structuralScopeFootnote: draft.structuralScopeFootnote.trim() || null,
        civilScopeFootnote: draft.civilScopeFootnote.trim() || null,
        noUnits: numberOfUnits,
        structuralFee: structuralFeePerUnit,
        civilFee: parseOptionalNumberInput(draft.civilFee, 'Civil fee'),
        soilReportFee: parseOptionalNumberInput(draft.soilReportFee, 'Soil report fee'),
        civil: parseOptionalNumberInput(draft.civil, 'Civil amount'),
        certificates: parseOptionalNumberInput(draft.certificates, 'Certificates'),
        adjustment: parseOptionalNumberInput(draft.adjustment, 'Adjustment'),
        structural: structuralTotal,
        structuralReferral: parseOptionalNumberInput(draft.structuralReferral, 'Structural referral'),
        structuralSubTotal: parseOptionalNumberInput(draft.structuralSubTotal, 'Structural subtotal'),
        subTotal: parseOptionalNumberInput(draft.subTotal, 'Subtotal'),
        client: draft.client.trim() || null,
        clientCompany: draft.clientCompany.trim() || null,
        abn: draft.abn.trim() || null,
        clientAddress: draft.clientAddress.trim() || null,
        clientMobile: normalizePhoneForStorage(draft.clientMobile),
        clientEmail: draft.clientEmail.trim() || null,
        exclusions,
        entity: draft.entity.trim() || null,
        senderDisplayName: draft.senderDisplayName.trim() || null,
        senderAddress: draft.senderAddress.trim() || null,
        senderMobile: normalizePhoneForStorage(draft.senderMobile),
        senderEmail: draft.senderEmail.trim() || null,
        senderPayId: draft.senderPayId.trim() || null,
        senderBsb: draft.senderBsb.trim() || null,
        senderAccountNumber: draft.senderAccountNumber.trim() || null,
        senderAccountName: draft.senderAccountName.trim() || null,
        referralName: draft.referralName.trim() || null,
        referralPercentage: (() => {
          const percent = parseOptionalNumberInput(draft.referralPercentage, 'Referral percentage');
          return percent === null ? null : percent / 100;
        })(),
        referralFee: parseOptionalNumberInput(draft.referralFee, 'Referral fee'),
        referralAmount: parseOptionalNumberInput(draft.referralAmount, 'Referral amount'),
        architectName: draft.architectName.trim() || null,
        architectCompany: draft.architectCompany.trim() || null,
        architectAbn: draft.architectAbn.trim() || null,
        architectAddress: draft.architectAddress.trim() || null,
        architectMobile: normalizePhoneForStorage(draft.architectMobile),
        architectEmail: draft.architectEmail.trim() || null,
      };

      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.updateQuotation(quotation.QuotationId, payload);

      let acceptResult: any = null;
      if (acceptanceTriggersWorkflow) {
        acceptResult = await azureSqlService.acceptWorkflowQuotation({
          quotationId: quotation.QuotationId,
          quotationNumber,
          createSharePointFolders: true,
        });
      }

      await loadQuotationDetails();
      setContactLookupRole(null);
      setContactLookupMatches([]);
      setContactLookupMessage('');
      setClosedContactLookupKey('');
      setIsEditing(false);
      if (acceptanceTriggersWorkflow) {
        const projectId = String(acceptResult?.projectId || '').trim();
        const projectNumber = String(acceptResult?.projectNumber || '').trim();
        const reusedLinkedProject = acceptResult?.reusedLinkedProject === true;
        const sharePointResult = acceptResult?.sharePointPlan;
        const sharePointMessage = String(sharePointResult?.message || '').trim();
        const sharePointStatus = String(sharePointResult?.integrationStatus || '').trim().toLowerCase();
        const sharePointFailed = sharePointStatus === 'error';
        const projectsFilterHint = currentUserEmail === WILSON_EMAIL
          ? ' Projects page may default to Civil only.'
          : currentUserEmail === YUAN_EMAIL
            ? ' Projects page may default to Structure only.'
            : '';

        setSaveNotice(
          reusedLinkedProject
            ? `Accepted quotation ${quotationNumber} and reused linked project ${projectNumber || projectId}. Existing tasks and invoices were preserved.`
            : projectNumber
              ? `Accepted quotation ${quotationNumber} and created project ${projectNumber}${projectId ? ` (#${projectId})` : ''}.${projectsFilterHint}`
              : `Accepted quotation ${quotationNumber} and created the linked project.${projectsFilterHint}`
        );

        if (sharePointFailed) {
          setSaveError(
            sharePointMessage
              ? `Project folder provisioning needs attention: ${sharePointMessage}`
              : 'Project folder provisioning needs attention.'
          );
        } else if (sharePointStatus === 'not_configured') {
          setSaveError(
            sharePointMessage
              ? `Project ${reusedLinkedProject ? 'reused' : 'created'}, but project folder provisioning is not configured: ${sharePointMessage}`
              : `Project ${reusedLinkedProject ? 'reused' : 'created'}, but project folder provisioning is not configured.`
          );
        } else if (sharePointMessage) {
          setSaveNotice((current) => current ? `${current} ${sharePointMessage}` : sharePointMessage);
        }
      } else {
        setSaveNotice(
          acceptanceReversal
            ? `Quotation ${quotationNumber} changed to ${nextAcceptance}. Existing signed records and linked project data were preserved.${normalizedNextAcceptance === 'pending' ? ' You can now issue a new signing link.' : ''}`
            : 'Quotation updated successfully.'
        );
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save quotation';
      setSaveError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!quotation || folderCreating || projectCreating || deleting) return;

    setSaveError(null);
    setSaveNotice(null);
    setFolderCreating(true);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const result = await azureSqlService.createWorkflowQuotationFolder({
        quotationId: quotation.QuotationId,
        quotationNumber: quotation.QuotationNumber,
      });

      const quotationFolderMetadata = result?.sharePoint?.quotationFolder || null;
      const folderPath = String(
        quotationFolderMetadata?.folderPath
        || result?.quotationProjectRoot
        || ''
      ).trim();
      const metadataPatch = {
        ...(folderPath ? { folderPath } : {}),
        ...(String(quotationFolderMetadata?.siteId || '').trim() ? { siteId: quotationFolderMetadata.siteId } : {}),
        ...(String(quotationFolderMetadata?.driveId || '').trim() ? { driveId: quotationFolderMetadata.driveId } : {}),
        ...(String(quotationFolderMetadata?.itemId || '').trim() ? { folderItemId: quotationFolderMetadata.itemId } : {}),
      };

      if (Object.keys(metadataPatch).length > 0) {
        try {
          await azureSqlService.updateQuotation(quotation.QuotationId, metadataPatch);
        } catch {
          // The server already tries to persist folder metadata; this is a client-side fallback only.
        }
      }

      await loadQuotationDetails();
      setSaveNotice(`Created quotation folder for ${quotation.QuotationNumber || quotation.QuotationId}.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setSaveError(`Failed to create quotation folder: ${message}`);
    } finally {
      setFolderCreating(false);
    }
  };

  const handleViewQuote = async () => {
    if (!quotation || quoteViewBusy || saving || folderCreating || projectCreating || projectFolderCreating || projectRestoring || deleting) return;

    const previewWindow = window.open('', '_blank');
    if (!previewWindow) {
      setSaveNotice(null);
      setSaveError('Allow pop-ups for PMApp, then select View Quote again.');
      return;
    }

    try {
      previewWindow.opener = null;
    } catch {
      // The browser may prevent changing opener; navigation still stays on the blob URL below.
    }

    setQuoteViewBusy(true);
    setSaveError(null);
    setSaveNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const result = await azureSqlService.getQuotationSavedPdf(quotation.QuotationId);
      const objectUrl = window.URL.createObjectURL(result.blob);
      previewWindow.location.href = objectUrl;
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      if (!previewWindow.closed) {
        previewWindow.close();
      }
      const message = err instanceof Error ? err.message : 'Failed to load the saved quotation PDF.';
      setSaveError(`Failed to view quote: ${message}`);
    } finally {
      setQuoteViewBusy(false);
    }
  };

  const handleIssueQuotationSigningLink = async () => {
    if (!quotation || signingLinkBusy || saving || folderCreating || projectCreating || projectFolderCreating || deleting) return;
    if (isAcceptedQuotationLike(quotation)) {
      setSigningLinkNotice(null);
      setSigningLinkError('Accepted quotations cannot issue a new public signing link.');
      return;
    }

    const quotationNumber = String(quotation.QuotationNumber || quotation.QuotationId || '').trim();
    const shouldConfirmReissue = Boolean(signingLinkResult);
    if (shouldConfirmReissue) {
      const confirmed = window.confirm(
        `Reissue the signing link for quotation ${quotationNumber}?\n\nThis revokes the current unused link and generates a new one.`
      );
      if (!confirmed) {
        return;
      }
    }

    setSigningLinkBusy(true);
    setSigningLinkError(null);
    setSigningLinkNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const issued = await azureSqlService.issueQuotationSigningLink({
        quotationId: quotation.QuotationId,
      });
      setSigningLinkResult(issued);
      setSigningLinkNotice(
        `${shouldConfirmReissue ? 'Reissued' : 'Issued'} the public signing link for quotation ${quotationNumber}. This raw link is only shown now because PMApp stores only its hash after creation.`
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to issue quotation signing link.';
      setSigningLinkError(message);
    } finally {
      setSigningLinkBusy(false);
    }
  };

  const handleCopySigningLink = async () => {
    if (!signingLinkResult?.signUrl) return;
    try {
      await navigator.clipboard.writeText(signingLinkResult.signUrl);
      setSigningLinkError(null);
      setSigningLinkNotice('Signing link copied to clipboard.');
    } catch {
      setSigningLinkError('Copy failed in this browser session. Open the link and copy it manually.');
    }
  };

  const handleDeleteQuotation = async () => {
    if (!quotation || deleting || folderCreating || projectCreating || projectFolderCreating) return;
    if (isAcceptedQuotationLike(quotation)) {
      setSaveNotice(null);
      setSaveError('Accepted quotations linked to projects cannot be deleted from the quotations screen.');
      return;
    }

    const quotationNumber = String(quotation.QuotationNumber || quotation.QuotationId || '').trim();
    if (!quotationNumber) {
      setSaveNotice(null);
      setSaveError('Quotation number is required before deleting this quotation.');
      return;
    }

    if (!confirmDeletionByTyping({
      entityLabel: `quotation ${quotationNumber}`,
      confirmationLabel: 'quotation number',
      identifiers: [quotationNumber],
      softDelete: true,
      extraMessage: 'This only marks the quotation row as deleted in the database. Deletion will be blocked if the quotation folder already exists in SharePoint.',
    })) {
      return;
    }

    setSaveError(null);
    setSaveNotice(null);
    setDeleting(true);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.deleteQuotation(quotation.QuotationId);
      setDeleting(false);
      navigate('/quotations');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setSaveError(`Failed to delete quotation: ${message}`);
      setDeleting(false);
    }
  };

  const draftStructuralFeePerUnitValue = getDraftStructuralFeePerUnitValue(draft);
  const draftStructuralFeePerUnitDisplay = draftStructuralFeePerUnitValue === null
    ? ''
    : String(draftStructuralFeePerUnitValue);
  const draftUnitCount = getDraftUnitCount(draft);
  const linkedProjectId = quotation
    ? Number(quotation.LinkedProjectId || quotation.ProjectId || 0) || null
    : null;
  const linkedProjectStatusLabel = String(linkedProject?.Status || '').trim();
  const linkedProjectDisplayNumber = String(
    linkedProject?.ProjectCode
    || linkedProject?.ProjectNumber
    || linkedProject?.ProjectId
    || linkedProjectId
    || ''
  ).trim();
  const linkedProjectBlocksRecovery = Boolean(
    linkedProjectId && (
      linkedProjectLoading
      || linkedProjectLookupFailed
      || (linkedProject ? !isArchivedProjectRecord(linkedProject) && isProjectStatusBlockingQuotationRecovery(linkedProject.Status) : false)
    )
  );
  const hasLinkedProject = Boolean(quotation && linkedProjectBlocksRecovery);
  const hasAcceptedLinkedProject = Boolean(
    quotation
    && linkedProject
    && linkedProjectId
    && isAcceptedQuotationLike(quotation)
    && isProjectStatusBlockingQuotationRecovery(linkedProject.Status)
  );
  const canRestoreLinkedProject = Boolean(linkedProject?.ProjectId && isArchivedProjectRecord(linkedProject));
  const acceptanceStatusLabel = quotation
    ? String(quotation.Acceptance || quotation.Status || (isAcceptedQuotationLike(quotation) ? 'Accepted' : '')).trim()
    : '';

  const handleCreateProjectFromQuotation = async () => {
    if (!quotation || hasLinkedProject || projectCreating || folderCreating || projectFolderCreating || deleting) return;

    const quotationNumber = String(quotation.QuotationNumber || quotation.QuotationId || '').trim();
    if (!quotationNumber) {
      setSaveNotice(null);
      setSaveError('Quotation number is required before creating a project.');
      return;
    }

    const confirmed = window.confirm(
      isAcceptedQuotationLike(quotation)
        ? `Create the missing linked project for quotation ${quotationNumber}?\n\nThis will create the project row and provision the project folders. It will stop if a linked project row or SharePoint project folder already exists.`
        : `Create project and folders from quotation ${quotationNumber}?\n\nThis will mark the quotation as Accepted, create the linked project row, and provision the project folders. It will stop if a linked project row or SharePoint project folder already exists.`
    );
    if (!confirmed) {
      return;
    }

    setProjectCreating(true);
    setSaveError(null);
    setSaveNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const result = await azureSqlService.createWorkflowProjectFromQuotation({
        quotationId: quotation.QuotationId,
        quotationNumber,
        createSharePointFolders: true,
      });

      await loadQuotationDetails();

      const projectId = String(result?.projectId || '').trim();
      const projectNumber = String(result?.projectNumber || '').trim();
      const sharePointResult = result?.sharePointPlan;
      const sharePointMessage = String(sharePointResult?.message || '').trim();
      const sharePointStatus = String(sharePointResult?.integrationStatus || '').trim().toLowerCase();
      const sharePointFailed = sharePointStatus === 'error';
      const projectsFilterHint = currentUserEmail === WILSON_EMAIL
        ? ' Projects page may default to Civil only.'
        : currentUserEmail === YUAN_EMAIL
          ? ' Projects page may default to Structure only.'
          : '';

      setSaveNotice(
        projectNumber
          ? `Created project ${projectNumber}${projectId ? ` (#${projectId})` : ''} from quotation ${quotationNumber}.${projectsFilterHint}`
          : `Created the linked project from quotation ${quotationNumber}.${projectsFilterHint}`
      );

      if (sharePointFailed) {
        setSaveError(
          sharePointMessage
            ? `Project folder provisioning needs attention: ${sharePointMessage}`
            : 'Project folder provisioning needs attention.'
        );
      } else if (sharePointStatus === 'not_configured') {
        setSaveError(
          sharePointMessage
            ? `Project created, but project folder provisioning is not configured: ${sharePointMessage}`
            : 'Project created, but project folder provisioning is not configured.'
        );
      } else if (sharePointMessage) {
        setSaveNotice((current) => current ? `${current} ${sharePointMessage}` : sharePointMessage);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      const statusCode = Number((err as { status?: unknown } | null)?.status);
      if (statusCode === 409) {
        setSaveError(message);
      } else {
        const refreshedQuotation = await loadQuotationDetails();
        const refreshedProjectId = Number(refreshedQuotation?.LinkedProjectId || refreshedQuotation?.ProjectId || 0) || null;
        if (refreshedProjectId && isAcceptedQuotationLike(refreshedQuotation)) {
          const projectLabel = quotationNumber || String(refreshedProjectId);
          setSaveNotice(
            `Created project ${projectLabel}${refreshedProjectId ? ` (#${refreshedProjectId})` : ''} from quotation ${quotationNumber}. ` +
            'The backend response failed after the action completed, so review the project folder if its status is unclear.'
          );
          setSaveError(null);
          return;
        }

        setSaveError(`Failed to create project from quotation: ${message}`);
      }
    } finally {
      setProjectCreating(false);
    }
  };

  const handleCreateProjectFolder = async () => {
    if (!quotation || !hasAcceptedLinkedProject || !linkedProjectId || projectFolderCreating || projectCreating || folderCreating || deleting) return;

    setProjectFolderCreating(true);
    setSaveError(null);
    setSaveNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const result = await azureSqlService.provisionProjectFolders(linkedProjectId);
      setFolderSetupRefresh(value => value + 1);
      const sharePoint = result?.sharePoint || null;
      const integrationStatus = String(sharePoint?.integrationStatus || '').trim().toLowerCase();
      const folderPath = sharePoint?.projectFolder?.folderPath || result?.civilProjectRoot || '';
      const detail = folderPath ? ` ${folderPath}` : '';
      const message = String(sharePoint?.message || '').trim();

      if (result?.job?.state === 'queued' || result?.job?.state === 'running' || integrationStatus === 'queued') {
        setSaveNotice(message || 'Project folder setup is running in the background. Check setup status below.');
      } else if (result?.job?.state === 'needs_attention') {
        setSaveNotice(message || 'Folder setup needs attention. Existing files are retained.');
      } else if (integrationStatus === 'configured' || sharePoint?.projectFolder) {
        setSaveNotice(`Created project folder for linked project ${result?.projectNumber || linkedProjectId}.${detail}`);
      } else if (message) {
        setSaveNotice(`Project folder provisioning returned: ${message}`);
      } else {
        setSaveNotice(`Project folder provisioning completed for linked project ${result?.projectNumber || linkedProjectId}.`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      const statusCode = Number((err as { status?: unknown } | null)?.status);
      if (statusCode === 409) {
        setSaveError(message);
      } else {
        setFolderSetupRefresh(value => value + 1);
        setSaveNotice(`Folder setup result could not be confirmed. Check its status before retrying. ${message}`);
      }
    } finally {
      setProjectFolderCreating(false);
    }
  };

  const handleOpenLinkedProject = () => {
    const targetProjectId = Number(linkedProject?.ProjectId || linkedProjectId || 0);
    if (!Number.isFinite(targetProjectId) || targetProjectId <= 0) {
      return;
    }

    navigate(`/projects/${Math.trunc(targetProjectId)}`);
  };

  const handleRestoreLinkedProject = async () => {
    const targetProjectId = Number(linkedProject?.ProjectId || linkedProjectId || 0);
    if (!quotation || !canRestoreLinkedProject || !Number.isFinite(targetProjectId) || targetProjectId <= 0 || projectRestoring || deleting) {
      return;
    }

    const quotationNumber = String(quotation.QuotationNumber || quotation.QuotationId || '').trim();
    const projectLabel = linkedProjectDisplayNumber || String(Math.trunc(targetProjectId));
    const confirmed = window.confirm(
      `Restore linked project ${projectLabel} for quotation ${quotationNumber}?\n\nThis changes the project status back to Active so you can continue using the existing project and create its folder if needed.`
    );
    if (!confirmed) {
      return;
    }

    setProjectRestoring(true);
    setSaveError(null);
    setSaveNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.updateProject(Math.trunc(targetProjectId), { status: 'Active' });
      await loadQuotationDetails();
      setSaveNotice(
        `Restored linked project ${projectLabel} to Active. You can now open the project or create its project folder.`
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setSaveError(`Failed to restore linked project: ${message}`);
    } finally {
      setProjectRestoring(false);
    }
  };

  const detailSections: DetailSection[] = quotation ? [
    {
      title: 'Basic Information',
      icon: '📋',
      accentColor: 'var(--color-primary)',
      fields: [
        { label: 'Quotation ID', value: resolvedQuotationRecordId || 'Not set' },
        {
          label: 'Quotation Number',
          value: isEditing ? renderDraftField('quotationNumber') : (resolvedQuotationNumber || 'Not set'),
        },
        {
          label: 'Date',
          value: isEditing ? renderDraftField('date', { type: 'date' }) : formatFieldValue(quotation.Date),
        },
        {
          label: 'Status (Acceptance)',
          value: isEditing ? (
            <div style={{ display: 'grid', gap: '8px' }}>
              <select
                aria-label="Status (Acceptance)"
                value={draft?.acceptance || ''}
                onChange={(event) => updateDraft('acceptance', event.target.value)}
                style={inputStyle}
              >
                <option value="">Not set</option>
                <option value="Pending">Pending</option>
                <option value="Accepted">Accepted</option>
                <option value="Rejected">Rejected</option>
              </select>
              {isAcceptedQuotationLike(quotation) ? (
                <div className="dgf-inline-help">
                  Change this to <strong>Pending</strong> or <strong>Rejected</strong> only when acceptance needs to be reversed. Existing signed documents and linked project records will be preserved.
                </div>
              ) : (draft?.acceptance || '').trim().toLowerCase() === 'accepted' ? (
                <div className="dgf-inline-help">
                  Saving with <strong>Accepted</strong> will create or reuse the linked project and run the current project folder workflow.
                </div>
              ) : null}
            </div>
          ) : acceptanceStatusLabel ? <StatusBadge status={acceptanceStatusLabel} /> : 'Not set',
        },
        {
          label: 'Brought By',
          value: isEditing ? renderDraftField('broughtBy') : (quotation.BroughtBy || 'Not set'),
        },
        {
          label: 'Certified By',
          value: isEditing ? renderDraftField('certifiedBy') : (quotation.CertifiedBy || 'Not set'),
        },
      ],
    },
    {
      title: 'Project Information',
      icon: '🏗️',
      accentColor: 'var(--color-info)',
      fields: [
        {
          label: 'Address',
          value: isEditing ? renderDraftField('address', { type: 'textarea', rows: 3 }) : (quotation.Address || 'Not set'),
          wrap: true,
        },
        {
          label: 'Suburb',
          value: isEditing ? (
            <div style={{ display: 'grid', gap: '8px' }}>
              <input
                list="quotation-detail-suburb-options"
                value={draft?.suburb || ''}
                onChange={(event) => {
                  updateDraft('suburb', event.target.value);
                  setSuburbLookupMessage('');
                }}
                onBlur={(event) => { void hydrateSuburbLookup(event.target.value); }}
                style={inputStyle}
              />
              <datalist id="quotation-detail-suburb-options">
                {suburbOptions.map((option) => (
                  <option key={`${option.Suburb}-${option.PostCode}`} value={option.Suburb}>
                    {option.PostCode}{option.Council ? ` - ${option.Council}` : ''}
                  </option>
                ))}
              </datalist>
              {suburbLookupMessage ? (
                <div className="dgf-inline-help">
                  {suburbLookupMessage}
                </div>
              ) : null}
            </div>
          ) : (formatSuburbLabel(quotation.Suburb) || 'Not set'),
        },
        {
          label: 'Post Code',
          value: isEditing ? (
            <input
              value={draft?.postCode || ''}
              onChange={(event) => {
                updateDraft('postCode', event.target.value);
                setSuburbLookupMessage('');
              }}
              onBlur={(event) => { void hydratePostCodeLookup(event.target.value); }}
              style={inputStyle}
            />
          ) : (quotation.PostCode || 'Not set'),
        },
        {
          label: 'Council',
          value: isEditing ? renderDraftField('council') : (quotation.Council || 'Not set'),
        },
        {
          label: 'Project Scope',
          value: isEditing ? (
            <select
              value={draft?.projectScope || ''}
              onChange={(event) => updateDraft('projectScope', event.target.value)}
              style={inputStyle}
            >
              <option value="">Select project scope</option>
              {quotationTemplateDefinition.projectScopeOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          ) : (quotation.ProjectScope || 'Not set'),
          wrap: true,
        },
        {
          label: 'Project Type',
          value: isEditing ? (
            <>
              <input
                list="quotation-detail-project-type-options"
                value={draft?.projectType || ''}
                onChange={(event) => updateDraft('projectType', event.target.value)}
                style={inputStyle}
                placeholder="Select or type project type"
              />
              <datalist id="quotation-detail-project-type-options">
                {quotationTemplateDefinition.projectTypeOptions.map((option) => (
                  <option key={option} value={option} />
                ))}
              </datalist>
            </>
          ) : (quotation.ProjectType || 'Not set'),
          wrap: true,
        },
        {
          label: 'Project Description',
          value: isEditing ? renderDraftField('projectDescription') : (quotation.ProjectDescription || 'Not set'),
          wrap: true,
        },
        {
          label: 'Structural Special Footnote',
          value: isEditing ? renderDraftField('structuralScopeFootnote', { type: 'textarea', rows: 3 }) : (quotation.ScopeFootnoteS || quotation.Scope_footnote_S || 'Not set'),
          wrap: true,
        },
        {
          label: 'Civil Special Footnote',
          value: isEditing ? renderDraftField('civilScopeFootnote', { type: 'textarea', rows: 3 }) : (quotation.ScopeFootnoteC || quotation.Scope_footnote_C || 'Not set'),
          wrap: true,
        },
        {
          label: 'Number of Units',
          value: isEditing ? renderDraftField('noUnits', { type: 'number' }) : (quotation.NoUnits || 'Not set'),
        },
      ],
    },
    {
      title: 'Financial Information',
      icon: '💰',
      accentColor: 'var(--color-success)',
      fields: [
        {
          label: 'Structural Fee Total',
          value: isEditing
            ? renderDraftField('structuralTotal', { type: 'number' })
            : formatAmount(getQuotationStructuralFeeTotal(quotation)),
        },
        {
          label: 'Structural Fee Per Unit',
          value: isEditing
            ? renderComputedDraftField(
              draftStructuralFeePerUnitDisplay,
              `Calculated automatically from the structural total across ${draftUnitCount} unit${draftUnitCount === 1 ? '' : 's'}.`
            )
            : formatAmount(getQuotationStructuralFeePerUnit(quotation)),
        },
        { label: 'Civil Fee', value: isEditing ? renderDraftField('civilFee', { type: 'number' }) : formatAmount(quotation.CivilFee) },
        { label: 'Soil Report Fee', value: isEditing ? renderDraftField('soilReportFee', { type: 'number' }) : formatAmount(quotation.SoilReportFee) },
        { label: 'Civil', value: isEditing ? renderDraftField('civil', { type: 'number' }) : formatAmount(quotation.Civil) },
        { label: 'Certificates', value: isEditing ? renderDraftField('certificates', { type: 'number' }) : formatAmount(quotation.Certificates) },
        { label: 'Adjustment', value: isEditing ? renderDraftField('adjustment', { type: 'number' }) : formatAmount(quotation.Adjustment) },
        { label: 'Structural Referral', value: isEditing ? renderDraftField('structuralReferral', { type: 'number' }) : formatAmount(quotation.StructuralReferral) },
        { label: 'Structural SubTotal', value: isEditing ? renderDraftField('structuralSubTotal', { type: 'number' }) : formatAmount(quotation.StructuralSubTotal) },
        { label: 'SubTotal', value: isEditing ? renderDraftField('subTotal', { type: 'number' }) : formatAmount(quotation.SubTotal), emphasize: true },
      ],
    },
    {
      title: 'Client Information',
      icon: '👤',
      accentColor: 'var(--color-info)',
      fields: [
        { label: 'Client Name', value: isEditing ? renderDraftField('client') : (quotation.Client || 'Not set') },
        { label: 'Client Company', value: isEditing ? renderDraftField('clientCompany') : (quotation.ClientCompany || 'Not set') },
        { label: 'ABN', value: isEditing ? renderDraftField('abn') : (quotation.ABN || 'Not set') },
        { label: 'Client Address', value: isEditing ? renderDraftField('clientAddress', { type: 'textarea', rows: 3 }) : (quotation.ClientAddress || 'Not set'), wrap: true },
        {
          label: 'Client Mobile',
          value: isEditing
            ? (
              <input
                type="tel"
                value={draft?.clientMobile || ''}
                onChange={(event) => updateDraft('clientMobile', event.target.value)}
                onBlur={() => formatDraftPhoneField('clientMobile')}
                style={inputStyle}
              />
            )
            : (formatPhoneForDisplay(quotation.ClientMobile) || 'Not set'),
        },
        { label: 'Client Email', value: isEditing ? renderDraftField('clientEmail', { type: 'email' }) : (quotation.ClientEmail || 'Not set'), wrap: true },
      ],
    },
    {
      title: 'Referral Information',
      icon: '🤝',
      accentColor: 'var(--color-warning)',
      fields: [
        { label: 'Referral Name', value: isEditing ? renderContactLookupDraftField('referral') : (quotation.ReferralName || 'Not set') },
        {
          label: 'Referral Percentage',
          value: isEditing ? renderDraftField('referralPercentage', { type: 'number' }) : (quotation.ReferralPercentage ? formatPercentage(quotation.ReferralPercentage) : 'Not set'),
        },
        { label: 'Referral Fee', value: isEditing ? renderDraftField('referralFee', { type: 'number' }) : formatAmount(quotation.ReferralFee) },
        { label: 'Referral Amount', value: isEditing ? renderDraftField('referralAmount', { type: 'number' }) : formatAmount(quotation.ReferralAmount) },
      ],
    },
    {
      title: 'Architect Information',
      icon: '🏛️',
      accentColor: 'var(--color-primary)',
      fields: [
        { label: 'Architect Name', value: isEditing ? renderContactLookupDraftField('architect') : (quotation.ArchitectName || 'Not set') },
        { label: 'Architect Company', value: isEditing ? renderDraftField('architectCompany') : (quotation.ArchitectCompany || 'Not set') },
        { label: 'Architect ABN', value: isEditing ? renderDraftField('architectAbn') : (quotation.ArchitectABN || 'Not set') },
        { label: 'Architect Address', value: isEditing ? renderDraftField('architectAddress', { type: 'textarea', rows: 3 }) : (quotation.ArchitectAddress || 'Not set'), wrap: true },
        {
          label: 'Architect Mobile',
          value: isEditing
            ? (
              <input
                type="tel"
                value={draft?.architectMobile || ''}
                onChange={(event) => updateDraft('architectMobile', event.target.value)}
                onBlur={() => formatDraftPhoneField('architectMobile')}
                style={inputStyle}
              />
            )
            : (formatPhoneForDisplay(quotation.ArchitectMobile) || 'Not set'),
        },
        { label: 'Architect Email', value: isEditing ? renderDraftField('architectEmail', { type: 'email' }) : (quotation.ArchitectEmail || 'Not set'), wrap: true },
      ],
    },
    {
      title: 'SharePoint Folder',
      icon: '📁',
      accentColor: 'var(--color-success)',
      fields: [
        { label: 'Folder Path', value: quotation.FolderPath || 'Not set', wrap: true },
        { label: 'Site ID', value: quotation.SiteId || 'Not set', wrap: true },
        { label: 'Drive ID', value: quotation.DriveId || 'Not set', wrap: true },
        { label: 'Folder Item ID', value: quotation.FolderItemId || 'Not set', wrap: true },
      ],
    },
    {
      title: 'Metadata',
      icon: 'ℹ️',
      accentColor: 'var(--color-text-muted)',
      fields: [
        { label: 'Created Date', value: formatFieldValue(quotation.CreatedDate) },
        { label: 'Modified Date', value: formatFieldValue(quotation.ModifiedDate) },
      ],
    },
  ] : [];

  return (
    <div className="dgf-page">
      <div className="dgf-detail-header">
        <div className="dgf-detail-heading">
          <button className="dgf-detail-back" aria-label="Back to quotations" onClick={() => navigate('/quotations')}>
            <ChevronLeft size={20} aria-hidden="true" />
          </button>
          <h1>{quotation ? `Quotation ${resolvedQuotationNumber || resolvedQuotationRecordId || quotationId}` : `Quotation ${quotationId || 'Details'}`}</h1>
        </div>
        {!loading && !error && quotation ? (
          <div className={`dgf-detail-actions${isEditing ? '' : ' dgf-detail-actions--primary'}`}>
            {isEditing ? (
              <>
                <Button size="sm" variant="secondary" onClick={handleCancelEditing} disabled={saving}>
                  Cancel
                </Button>
                <Button size="sm" variant="primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="secondary" aria-label="View Quote" iconBefore={<FileText size={18} aria-hidden="true" />}
                  onClick={handleViewQuote}
                  disabled={quoteViewBusy || folderCreating || projectCreating || projectFolderCreating || projectRestoring || deleting || saving}
                  title="Open the latest saved quotation PDF in a new tab.">
                  {quoteViewBusy ? 'Opening...' : 'View'}
                </Button>
                <Button size="sm" variant="secondary" aria-label="Edit Quotation" iconBefore={<Pencil size={18} aria-hidden="true" />}
                  onClick={handleStartEditing} disabled={quoteViewBusy || folderCreating || projectCreating || projectFolderCreating || projectRestoring || deleting}>
                  Edit
                </Button>
                {linkedProjectId ? (
                  <Button size="sm" variant="secondary" aria-label="Open Linked Project" iconBefore={<Building2 size={18} aria-hidden="true" />}
                    onClick={handleOpenLinkedProject}
                    disabled={folderCreating || projectCreating || projectFolderCreating || projectRestoring || deleting || linkedProjectLoading}
                    title="Open the linked project detail page, even if the project is currently cancelled.">
                    Project
                  </Button>
                ) : null}
                <DetailActionMenu label="More quotation actions" items={[
                  {
                    label: 'Amend Quotation',
                    icon: <FilePenLine aria-hidden="true" />,
                    onClick: handleAmendInWorkflow,
                    disabled: !resolvedQuotationRecordId || quoteViewBusy || folderCreating || projectCreating || projectFolderCreating || projectRestoring || deleting || saving,
                    title: 'Open this quotation in the quotation workflow with its saved data loaded for amendment.',
                  },
                  {
                    label: signingLinkBusy ? 'Preparing Signing Link...' : signingLinkResult ? 'Reissue Signing Link' : 'Issue Signing Link',
                    icon: <Link2 aria-hidden="true" />,
                    onClick: handleIssueQuotationSigningLink,
                    disabled: folderCreating || projectCreating || projectFolderCreating || projectRestoring || deleting || saving || signingLinkBusy || isAcceptedQuotationLike(quotation),
                    title: isAcceptedQuotationLike(quotation) ? 'Accepted quotations cannot issue a new public signing link.' : 'Issue a one-time public signing link for the client.',
                  },
                  ...(signingLinkResult ? [
                    {
                      label: 'Copy Signing Link',
                      icon: <Copy aria-hidden="true" />,
                      onClick: handleCopySigningLink,
                      disabled: signingLinkBusy,
                      title: 'Copy the latest issued public signing link.',
                    },
                    {
                      label: 'Open Signing Page',
                      icon: <ExternalLink aria-hidden="true" />,
                      onClick: () => { window.open(signingLinkResult.signUrl, '_blank', 'noopener,noreferrer'); },
                      disabled: signingLinkBusy,
                      title: 'Open the latest issued public signing link in a new tab.',
                    },
                  ] : []),
                  ...(canRestoreLinkedProject ? [{
                    label: projectRestoring ? 'Restoring Project...' : 'Restore Linked Project',
                    icon: <RotateCcw aria-hidden="true" />,
                    onClick: handleRestoreLinkedProject,
                    disabled: folderCreating || projectCreating || projectFolderCreating || projectRestoring || deleting,
                    title: 'Restore the cancelled linked project by setting its status back to Active.',
                  }] : []),
                  ...(!hasLinkedProject ? [{
                    label: projectCreating ? 'Creating Project...' : 'Create Project + Folder',
                    icon: <Building2 aria-hidden="true" />,
                    onClick: handleCreateProjectFromQuotation,
                    disabled: folderCreating || projectCreating || projectFolderCreating || projectRestoring || deleting || linkedProjectLoading,
                    title: 'Fallback action to create the missing linked project row and project folders. Cancelled or deleted linked projects no longer block this recovery path.',
                  }] : []),
                  ...(hasAcceptedLinkedProject ? [{
                    label: projectFolderCreating ? 'Creating Project Folder...' : 'Create Project Folder',
                    icon: <FolderPlus aria-hidden="true" />,
                    onClick: handleCreateProjectFolder,
                    disabled: folderCreating || projectCreating || projectFolderCreating || projectRestoring || deleting,
                    title: 'Fallback action for accepted quotations. It creates the linked project folder only when SharePoint does not already have that project folder.',
                  }] : []),
                  {
                    label: folderCreating ? 'Creating Folder...' : 'Create Quotation Folder',
                    icon: <FolderPlus aria-hidden="true" />,
                    onClick: handleCreateFolder,
                    disabled: folderCreating || projectCreating || projectFolderCreating || projectRestoring || deleting,
                  },
                  {
                    label: deleting ? 'Deleting...' : 'Delete Quotation',
                    icon: <Trash2 aria-hidden="true" />,
                    onClick: handleDeleteQuotation,
                    disabled: folderCreating || projectCreating || projectFolderCreating || projectRestoring || deleting || isAcceptedQuotationLike(quotation),
                    title: isAcceptedQuotationLike(quotation) ? 'Accepted quotations linked to projects cannot be deleted.' : 'Delete quotation',
                    danger: true,
                  },
                ]} />
              </>
            )}
          </div>
        ) : null}
        {!loading && !error && quotation && (signingLinkResult || signingLinkNotice || signingLinkError) ? (
          <div style={{ marginTop: '12px', display: 'grid', gap: '10px' }}>
            {signingLinkNotice ? (
              <div className="dgf-notice dgf-notice--info">
                {signingLinkNotice}
              </div>
            ) : null}
            {signingLinkError ? (
              <div className="dgf-notice dgf-notice--danger">
                {signingLinkError}
              </div>
            ) : null}
            {signingLinkResult ? (
              <div className="dgf-notice dgf-notice--success" style={{ display: 'grid', gap: '8px' }}>
                <strong>Public signing link</strong>
                <span>{signingLinkResult.signUrl}</span>
                <span>
                  Issued {new Date(signingLinkResult.issuedAt).toLocaleString('en-AU')}
                  {' '}and expires {new Date(signingLinkResult.expiresAt).toLocaleString('en-AU')}.
                </span>
                <span>Reissuing generates a fresh raw link and revokes the previous unused one.</span>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {loading && (
        <div className="dgf-page__content">
          <div className="dgf-state-card">
            <div className="dgf-state-card__icon">⏳</div>
            <div>Loading quotation details...</div>
          </div>
        </div>
      )}

      {error && (
        <div className="dgf-page__content">
          <div className="dgf-state-card">
            <div className="dgf-state-card__icon">❌</div>
            <div className="dgf-state-card__title">Error</div>
            <div className="dgf-notice dgf-notice--danger" style={{ marginTop: 16 }}>{error}</div>
            <Button onClick={() => loadQuotationDetails()} variant="primary" style={{ marginTop: '16px' }}>
              Retry
            </Button>
          </div>
        </div>
      )}

      {!loading && !error && quotation && (
        <div className="dgf-page__content">
          {linkedProjectId && linkedProjectLookupFailed ? (
            <div className="dgf-notice dgf-notice--warning">
              Linked project #{linkedProjectId} could not be checked, so quotation recovery actions stay locked until the project can be loaded.
            </div>
          ) : null}
          {linkedProjectId && linkedProject && canRestoreLinkedProject ? (
            <div className="dgf-notice dgf-notice--info">
              Quotation {quotation.QuotationNumber || quotation.QuotationId} is still linked to an archived project {linkedProjectDisplayNumber || linkedProjectId}
              {linkedProjectStatusLabel ? ` (${linkedProjectStatusLabel})` : ''}. Restore that project or create a fresh replacement project and folder from this quotation.
            </div>
          ) : null}
          <ProjectFolderSetupNotice projectId={linkedProjectId} enabled={hasRequiredRoleAccess(user?.userRoles, user?.userDetails, ['pm', 'admin'])} refreshKey={folderSetupRefresh} onReady={() => { void loadQuotationDetails(true); }} />
          {saveError ? (
            <div className="dgf-notice dgf-notice--danger">
              {saveError}
            </div>
          ) : null}
          {saveNotice ? (
            <div className="dgf-notice dgf-notice--success">
              {saveNotice}
            </div>
          ) : null}

          {detailSections.map((section) => (
            <FormSection
              key={section.title}
              title={section.title}
              icon={section.icon}
              accentColor={section.accentColor}
              style={{ overflow: 'visible' }}
            >
              <DetailFieldList fields={section.fields} className={isEditing ? '' : 'ui-detail-field-list--inline'} />
            </FormSection>
          ))}
        </div>
      )}

      <div style={{ height: '80px' }} />
    </div>
  );
};

export default QuotationDetail;
